import React from 'react';
import { Badge, Tooltip } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import AppIconButton from './AppIconButton';

export interface CompactFilterIconButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  activeCount?: number;
  /** Popover/menu open — highlight the control. */
  open?: boolean;
  /** Filters applied without a numeric count (e.g. custom filter popover). */
  active?: boolean;
  disabled?: boolean;
  tooltip?: string;
  'aria-label'?: string;
}

/**
 * Icon-only filter control for toolbars. Shows a badge when `activeCount` > 0.
 */
const CompactFilterIconButton: React.FC<CompactFilterIconButtonProps> = ({
  onClick,
  activeCount = 0,
  open = false,
  active = false,
  disabled = false,
  tooltip,
  'aria-label': ariaLabel = 'Filters',
}) => {
  const highlighted = open || active || activeCount > 0;
  const tooltipTitle =
    tooltip ??
    (activeCount > 0
      ? `Filters (${activeCount} active)`
      : active
        ? 'Filters active'
        : 'Filters');

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <Badge
          badgeContent={activeCount}
          color="primary"
          invisible={activeCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 16,
              minWidth: 16,
              padding: '0 4px',
            },
          }}
        >
          <AppIconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            aria-label={
              activeCount > 0 ? `${ariaLabel}, ${activeCount} active` : ariaLabel
            }
            color={highlighted ? 'primary' : 'default'}
          >
            <FilterList fontSize="small" />
          </AppIconButton>
        </Badge>
      </span>
    </Tooltip>
  );
};

export default CompactFilterIconButton;
